import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { InputText } from 'primereact/inputtext';
import { useNavigate, useParams } from 'react-router-dom';
import { InputTextarea } from 'primereact/inputtextarea';
import api from '../../../services/api';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { LANG_OPTIONS, PAGE_TYPES, BORDER_OPTIONS, tagOptions, BORDER_STATES, BORDER_CITIES_BY_STATE, TERMINOLOGY_OPTIONS, TERMINOLOGY_GENDER_OPTIONS, resolveTerminology, borderPathSegment } from '../../../constants/constants';
import { Controller, useForm } from 'react-hook-form';
import SeoLiveCodePreview from "./SeoLiveCodePreview";
import MetaTagsLiveCodePreview from './MetaTagsLiveCodePreview';
import { Panel } from 'primereact/panel';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ProgressBar } from 'primereact/progressbar';
import './styles.css'; // Tus estilos CSS
import ProseMirrorComponent from '../../../components/AdvancedProseMirrorEditor';
import TranslationsManager from '../../../components/TranslationsManager';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import {
  CUSTOM_TRANSPORT_ICONS,
  DEFAULT_CUSTOM_TRANSPORT_ICON,
  customTransportIconSrc,
  normalizeCustomTransportIcon,
} from '../../../constants/customTransportIcons';

const BUILTIN_TRANSPORT_MODES = [
  {
    transport_mode: 'commercial',
    custom: false,
    defaultSources: ['cbp', 'community'],
    lane_types: [{ type: 'standard' }, { type: 'fast' }],
  },
  {
    transport_mode: 'passenger',
    custom: false,
    defaultSources: ['cbp', 'community'],
    lane_types: [{ type: 'standard' }, { type: 'sentri' }, { type: 'ready' }],
  },
  {
    transport_mode: 'pedestrian',
    custom: false,
    defaultSources: ['cbp', 'community'],
    lane_types: [{ type: 'standard' }, { type: 'ready' }],
  },
];

const RESERVED_TRANSPORT_KEYS = new Set(BUILTIN_TRANSPORT_MODES.map((m) => m.transport_mode));
const INHERIT_LANES_BY_TRANSPORT = {
  passenger: ['standard', 'sentri', 'ready'],
  pedestrian: ['standard', 'ready'],
  commercial: ['standard', 'fast'],
};
const DAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];
const DEFAULT_OPEN_DAYS = [1, 2, 3, 4, 5];

function defaultSyntheticRanges(openDays = DEFAULT_OPEN_DAYS) {
  const days = openDays.length ? openDays : DEFAULT_OPEN_DAYS;
  return [
    { days: [...days], start: '06:00', end: '09:00', min: 25, max: 40 },
    { days: [...days], start: '09:00', end: '21:00', min: 10, max: 20 },
    { days: [...days], start: '21:00', end: '06:00', min: 5, max: 12 },
  ];
}

function defaultInheritFrom() {
  return { transport_mode: 'passenger', lane_type: 'sentri' };
}

function defaultSyntheticConfig() {
  return {
    factorMin: 0.65,
    factorMax: 1.05,
    openDays: [...DEFAULT_OPEN_DAYS],
    ranges: defaultSyntheticRanges(),
  };
}

function normalizeOpenDays(raw) {
  if (!Array.isArray(raw)) return [...DEFAULT_OPEN_DAYS];
  const days = [
    ...new Set(
      raw
        .map((value) => Number(value))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
    ),
  ];
  return days;
}

function pruneRangesToOpenDays(ranges, openDays) {
  const allowed = new Set(openDays);
  return (ranges || [])
    .map((row) => ({
      ...row,
      days: (Array.isArray(row.days) ? row.days : []).filter((day) => allowed.has(day)),
    }))
    .filter((row) => Array.isArray(row.days) && row.days.length > 0);
}

function emptyLangLabels() {
  return Object.fromEntries((LANG_OPTIONS || []).map((lang) => [lang.code, '']));
}

function slugifyTransportKey(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toCustomModeConfig(custom) {
  const inheritFrom = {
    transport_mode: custom.inheritFrom?.transport_mode || 'passenger',
    lane_type: custom.inheritFrom?.lane_type || 'sentri',
  };
  const openDays = normalizeOpenDays(custom.synthetic?.openDays);
  return {
    transport_mode: custom.key,
    custom: true,
    defaultSources: ['synthetic'],
    lane_types: [{ type: inheritFrom.lane_type }],
    labels: custom.labels || {},
    icon: normalizeCustomTransportIcon(custom.icon, custom.key),
    inheritFrom,
    synthetic: {
      factorMin: Number(custom.synthetic?.factorMin) || 0.65,
      factorMax: Number(custom.synthetic?.factorMax) || 1.05,
      openDays,
      ranges: Array.isArray(custom.synthetic?.ranges) && custom.synthetic.ranges.length
        ? pruneRangesToOpenDays(custom.synthetic.ranges, openDays)
        : defaultSyntheticRanges(openDays),
    },
  };
}

function modesForSection(section) {
  const customs = (section?.customTransportModes || []).map(toCustomModeConfig);
  return [...BUILTIN_TRANSPORT_MODES, ...customs];
}

function customModesForSection(section) {
  return (section?.customTransportModes || []).map(toCustomModeConfig);
}

function formatCustomLangLabels(labels) {
  return (LANG_OPTIONS || [])
    .map((lang) => String(labels?.[lang.code] || '').trim())
    .filter(Boolean)
    .join(', ');
}

function CustomTransportDialog({
  visible,
  editingKey,
  initialKey,
  initialLabels,
  initialIcon,
  initialInheritFrom,
  initialSynthetic,
  onHide,
  onSave,
}) {
  const [key, setKey] = useState('');
  const [labels, setLabels] = useState(() => emptyLangLabels());
  const [icon, setIcon] = useState(DEFAULT_CUSTOM_TRANSPORT_ICON);
  const [inheritFrom, setInheritFrom] = useState(() => defaultInheritFrom());
  const [synthetic, setSynthetic] = useState(() => defaultSyntheticConfig());

  useEffect(() => {
    if (!visible) return;
    setKey(initialKey || '');
    setLabels({ ...emptyLangLabels(), ...(initialLabels || {}) });
    setIcon(normalizeCustomTransportIcon(initialIcon, initialKey));
    setInheritFrom({ ...defaultInheritFrom(), ...(initialInheritFrom || {}) });
    const openDays = normalizeOpenDays(initialSynthetic?.openDays);
    const nextSynthetic = {
      ...defaultSyntheticConfig(),
      ...(initialSynthetic || {}),
      openDays,
      ranges: Array.isArray(initialSynthetic?.ranges) && initialSynthetic.ranges.length
        ? pruneRangesToOpenDays(initialSynthetic.ranges, openDays)
        : defaultSyntheticRanges(openDays),
    };
    setSynthetic(nextSynthetic);
  }, [visible, editingKey, initialKey]);

  const inheritLanes = INHERIT_LANES_BY_TRANSPORT[inheritFrom.transport_mode] || ['sentri'];
  const openDays = normalizeOpenDays(synthetic.openDays);
  const openDayOptions = DAY_OPTIONS.filter((day) => openDays.includes(day.value));

  const setInheritTransport = (transport_mode) => {
    const lanes = INHERIT_LANES_BY_TRANSPORT[transport_mode] || ['sentri'];
    setInheritFrom({
      transport_mode,
      lane_type: lanes.includes(inheritFrom.lane_type) ? inheritFrom.lane_type : lanes[0],
    });
  };

  const toggleOpenDay = (day, checked) => {
    setSynthetic((prev) => {
      const current = normalizeOpenDays(prev.openDays);
      const nextOpen = checked
        ? current.includes(day) ? current : [...current, day]
        : current.filter((value) => value !== day);
      const ranges = pruneRangesToOpenDays(prev.ranges, nextOpen);
      return {
        ...prev,
        openDays: nextOpen,
        ranges: ranges.length ? ranges : (nextOpen.length ? defaultSyntheticRanges(nextOpen) : []),
      };
    });
  };

  const updateRange = (index, patch) => {
    setSynthetic((prev) => {
      const ranges = [...(prev.ranges || [])];
      ranges[index] = { ...ranges[index], ...patch };
      return { ...prev, ranges };
    });
  };

  const toggleRangeDay = (index, day, checked) => {
    setSynthetic((prev) => {
      if (!normalizeOpenDays(prev.openDays).includes(day)) return prev;
      const ranges = [...(prev.ranges || [])];
      const current = Array.isArray(ranges[index]?.days) ? [...ranges[index].days] : [];
      const next = checked
        ? current.includes(day) ? current : [...current, day]
        : current.filter((value) => value !== day);
      ranges[index] = { ...ranges[index], days: next };
      return { ...prev, ranges };
    });
  };

  return (
    <Dialog
      header={editingKey ? 'Edit custom transport' : 'Custom Transport'}
      visible={visible}
      style={{ width: '42rem' }}
      onHide={onHide}
      footer={(
        <div>
          <Button type="button" label="Cancel" className="p-button-text" onClick={onHide} />
          <Button
            type="button"
            label="Save"
            onClick={() => onSave({ key, labels, icon, inheritFrom, synthetic })}
          />
        </div>
      )}
    >
      <div className="p-fluid">
        <label className="font-semibold">Key</label>
        <InputText
          className="mb-3 mt-1"
          value={key}
          disabled={Boolean(editingKey)}
          placeholder="motorcycle"
          onChange={(e) => setKey(e.target.value)}
        />
        <small className="text-500 block mb-3">Stable slug stored in visibility (not passenger / pedestrian / commercial).</small>
        {LANG_OPTIONS.map((lang) => (
          <div key={lang.code} className="mb-3">
            <label className="font-semibold">Label {lang.code}</label>
            <InputText
              className="mt-1"
              value={labels?.[lang.code] || ''}
              placeholder={lang.name}
              onChange={(e) => setLabels((prev) => ({ ...prev, [lang.code]: e.target.value }))}
            />
          </div>
        ))}

        <div className="mb-3 p-3 surface-100 border-round">
          <div className="font-semibold mb-2">Icon</div>
          <small className="text-500 block mb-3">Shown on web and mobile for this extra crossing.</small>
          <div className="grid">
            {CUSTOM_TRANSPORT_ICONS.map((option) => {
              const selected = icon === option.id;
              return (
                <div key={option.id} className="col-3 sm:col-2">
                  <button
                    type="button"
                    onClick={() => setIcon(option.id)}
                    title={option.label}
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0.65rem 0.35rem',
                      borderRadius: 10,
                      border: selected ? '2px solid #2563eb' : '1px solid #d1d5db',
                      background: selected ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={customTransportIconSrc(option.id)}
                      alt={option.label}
                      width={20}
                      height={20}
                    />
                    <span style={{ fontSize: 11, lineHeight: 1.2, textAlign: 'center' }}>{option.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-2 mb-3 p-3 surface-100 border-round">
          <div className="font-semibold mb-2">Inherit wait from</div>
          <small className="text-500 block mb-3">
            Extra crossings derive live minutes from this CBP transport + lane (e.g. motorcycle → passenger / SENTRI).
          </small>
          <div className="grid">
            <div className="col-6">
              <label className="font-semibold">Transport</label>
              <Dropdown
                className="w-full mt-1"
                value={inheritFrom.transport_mode}
                options={BUILTIN_TRANSPORT_MODES.map((mode) => ({
                  label: mode.transport_mode,
                  value: mode.transport_mode,
                }))}
                onChange={(e) => setInheritTransport(e.value)}
              />
            </div>
            <div className="col-6">
              <label className="font-semibold">Lane type</label>
              <Dropdown
                className="w-full mt-1"
                value={inheritFrom.lane_type}
                options={inheritLanes.map((lane) => ({ label: lane, value: lane }))}
                onChange={(e) => setInheritFrom((prev) => ({ ...prev, lane_type: e.value }))}
              />
            </div>
          </div>
        </div>

        <div className="mb-3 p-3 surface-100 border-round">
          <div className="font-semibold mb-2">Open days</div>
          <small className="text-500 block mb-3">
            Days this extra crossing operates. Unchecked days show as closed (no synthetic wait).
          </small>
          <div className="flex flex-wrap gap-3">
            {DAY_OPTIONS.map((day) => (
              <div key={`open-${day.value}`} className="flex align-items-center">
                <Checkbox
                  inputId={`open-day-${day.value}`}
                  checked={openDays.includes(day.value)}
                  onChange={(e) => toggleOpenDay(day.value, e.checked)}
                />
                <label htmlFor={`open-day-${day.value}`} className="ml-2 text-sm">{day.label}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3 p-3 surface-100 border-round">
          <div className="font-semibold mb-2">Synthetic wait</div>
          <small className="text-500 block mb-3">
            Factor vs inherited lane, then clamp to hour ranges on open days only. Uncheck source “Synthetic” on the port to stop generating.
          </small>
          <div className="grid">
            <div className="col-6">
              <label className="font-semibold">Factor min</label>
              <InputText
                className="mt-1"
                type="number"
                step="0.05"
                value={synthetic.factorMin}
                onChange={(e) => setSynthetic((prev) => ({ ...prev, factorMin: Number(e.target.value) }))}
              />
            </div>
            <div className="col-6">
              <label className="font-semibold">Factor max</label>
              <InputText
                className="mt-1"
                type="number"
                step="0.05"
                value={synthetic.factorMax}
                onChange={(e) => setSynthetic((prev) => ({ ...prev, factorMax: Number(e.target.value) }))}
              />
            </div>
          </div>
          <div className="flex justify-content-between align-items-center mt-3 mb-2">
            <span className="font-semibold">Hour ranges</span>
            <Button
              type="button"
              label="Add range"
              icon="pi pi-plus"
              className="p-button-text p-button-sm"
              disabled={!openDays.length}
              onClick={() => setSynthetic((prev) => ({
                ...prev,
                ranges: [
                  ...(prev.ranges || []),
                  {
                    days: [...normalizeOpenDays(prev.openDays)],
                    start: '09:00',
                    end: '21:00',
                    min: 10,
                    max: 20,
                  },
                ],
              }))}
            />
          </div>
          {!openDays.length ? (
            <small className="text-orange-600">Select at least one open day before adding hour ranges.</small>
          ) : null}
          {(synthetic.ranges || []).map((row, index) => (
            <div key={`range-${index}`} className="mb-3 p-2 border-round border-1 surface-border">
              <div className="flex justify-content-between align-items-center mb-2">
                <div className="flex flex-wrap gap-2">
                  {openDayOptions.map((day) => (
                    <div key={`${index}-${day.value}`} className="flex align-items-center">
                      <Checkbox
                        inputId={`${index}-day-${day.value}`}
                        checked={Array.isArray(row.days) && row.days.includes(day.value)}
                        onChange={(e) => toggleRangeDay(index, day.value, e.checked)}
                      />
                      <label htmlFor={`${index}-day-${day.value}`} className="ml-1 text-xs">{day.label}</label>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-text p-button-danger p-button-sm"
                  onClick={() => setSynthetic((prev) => ({
                    ...prev,
                    ranges: prev.ranges.filter((_, i) => i !== index),
                  }))}
                />
              </div>
              <div className="grid">
                <div className="col-3">
                  <label className="text-xs">Start</label>
                  <InputText className="mt-1" value={row.start || ''} onChange={(e) => updateRange(index, { start: e.target.value })} />
                </div>
                <div className="col-3">
                  <label className="text-xs">End</label>
                  <InputText className="mt-1" value={row.end || ''} onChange={(e) => updateRange(index, { end: e.target.value })} />
                </div>
                <div className="col-3">
                  <label className="text-xs">Min min</label>
                  <InputText className="mt-1" type="number" value={row.min ?? ''} onChange={(e) => updateRange(index, { min: Number(e.target.value) })} />
                </div>
                <div className="col-3">
                  <label className="text-xs">Max min</label>
                  <InputText className="mt-1" type="number" value={row.max ?? ''} onChange={(e) => updateRange(index, { max: Number(e.target.value) })} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function RouteBuilderComponent() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  // UI state
  const [, setIsLoadingPorts] = useState(false);
  const [, setLoadingCrossingNames] = useState(false);

  const [portOptions, setPortOptions] = useState([]);

  const [isLoadingEntryPage, setIsLoadingEntryPage] = useState(false);
  const [manualPortOptions, setManualPortOptions] = useState([]);

  const [, setCrossingNames] = useState([]);

  const CompletionIndicator = ({ percentage }) => {
    return (
      <div className="flex align-items-center gap-3">
        <span className={`text-sm font-medium ${percentage === 100 ? 'text-green-600' :
            percentage >= 70 ? 'text-blue-600' :
              'text-orange-600'
          }`}>
          {percentage === 100 ? `${percentage}% completed` : `${percentage}% progress`}
        </span>
        <ProgressBar
          value={percentage}
          style={{ width: '200px', height: '10px' }}
          showValue={false}
          color={percentage === 100 ? '#22c55e' : percentage >= 70 ? '#3b82f6' : '#f59e0b'}
        />
      </div>
    );
  };

  // const [metaTags, setMetaTags] = useState([]);
  const [metaTags, setMetaTags] = useState([
    { attributes: [{ key: '', value: '' }] }
  ]);
  // const [metaGlobalTags, setMetaGlobalTags] = useState([]);

  const [crossingSections, setCrossingSections] = useState([]);
  const [customTransportDialog, setCustomTransportDialog] = useState({
    visible: false,
    sectionIdx: null,
    editingKey: null,
    initialKey: '',
    initialLabels: emptyLangLabels(),
    initialIcon: DEFAULT_CUSTOM_TRANSPORT_ICON,
    initialInheritFrom: defaultInheritFrom(),
    initialSynthetic: defaultSyntheticConfig(),
  });


  // Fetch existing meta tags
  const fetchMetaTags = async () => {
    // setLoading(true);
    try {
      await api.get("/meta_tags");
      // console.log('Fetched meta tags:', data);
      // const tags = data.map(tag => ({
      //   ...tag.attributes,
      //   // isGlobal: true
      // }));
      // console.log('tags for props', data)
      // setMetaGlobalTags(data);
    } catch (error) {
      // showToast('error', 'Error', 'Could not load meta tags');
    }
    // setLoading(false);
  };

  useEffect(() => {
    fetchMetaTags();
  }, []);

  // Agregar un nuevo CrossingNameItem vacío
  const addCrossingSection = () => {
    console.log('Adding new crossing section', crossingSections);
    setCrossingSections([
      ...crossingSections,
      { name: "", mobileName: "", description: "", ports: [], lanes: [], customTransportModes: [] }
    ]);
  };

  // Actualizar campos name/description
  const updateCrossingField = (index, field, value) => {
    const updated = [...crossingSections];
    updated[index][field] = value;
    setCrossingSections(updated);
  };

  // Agregar un puerto al item correspondiente
  const handleAddPort = (index, port) => {
    if (!port) return;
    const updated = [...crossingSections];
    if (!updated[index].ports) updated[index].ports = [];
    const portNumber = String(port.port_number || '').trim();
    if (portNumber && updated[index].ports.some((p) => String(p.port_number || '').trim() === portNumber)) {
      return;
    }
    updated[index].ports.push({
      port_name: port.port_name || '',
      port_number: portNumber,
      visibility: {},
      sources: {},
      lanes: port.lanes || [],
    });
    setCrossingSections(updated);
  };

  // Eliminar un puerto
  const removePort = (index, port) => {
    const updated = [...crossingSections];
    updated[index].ports = updated[index].ports.filter(p => p !== port);
    setCrossingSections(updated);
  };

  const confirmRemovePort = (index, port) => {
    const name = port?.port_name || 'this port';
    const number = port?.port_number ? ` (${port.port_number})` : '';
    confirmDialog({
      header: 'Remove port',
      message: `Remove ${name}${number}? Lane visibility for this port will be lost until you add it again.`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      accept: () => removePort(index, port),
    });
  };

  const openAddCustomTransport = (sectionIdx) => {
    setCustomTransportDialog({
      visible: true,
      sectionIdx,
      editingKey: null,
      initialKey: '',
      initialLabels: emptyLangLabels(),
      initialIcon: DEFAULT_CUSTOM_TRANSPORT_ICON,
      initialInheritFrom: defaultInheritFrom(),
      initialSynthetic: defaultSyntheticConfig(),
    });
  };

  const openEditCustomTransport = (sectionIdx, custom) => {
    setCustomTransportDialog({
      visible: true,
      sectionIdx,
      editingKey: custom.key,
      initialKey: custom.key,
      initialLabels: { ...emptyLangLabels(), ...(custom.labels || {}) },
      initialIcon: normalizeCustomTransportIcon(custom.icon, custom.key),
      initialInheritFrom: { ...defaultInheritFrom(), ...(custom.inheritFrom || {}) },
      initialSynthetic: {
        ...defaultSyntheticConfig(),
        ...(custom.synthetic || {}),
        openDays: normalizeOpenDays(custom.synthetic?.openDays),
        ranges: Array.isArray(custom.synthetic?.ranges) && custom.synthetic.ranges.length
          ? pruneRangesToOpenDays(custom.synthetic.ranges, normalizeOpenDays(custom.synthetic?.openDays))
          : defaultSyntheticRanges(normalizeOpenDays(custom.synthetic?.openDays)),
      },
    });
  };

  const closeCustomTransportDialog = () => {
    setCustomTransportDialog((prev) => ({ ...prev, visible: false }));
  };

  const saveCustomTransport = ({ key: rawKey, labels, icon, inheritFrom, synthetic }) => {
    const sectionIdx = customTransportDialog.sectionIdx;
    if (sectionIdx == null) return;
    const key = slugifyTransportKey(rawKey);
    if (!key) {
      toast.current?.show({ severity: 'warn', summary: 'Key required', detail: 'Use a slug like motorcycle', life: 3000 });
      return;
    }
    if (RESERVED_TRANSPORT_KEYS.has(key)) {
      toast.current?.show({ severity: 'warn', summary: 'Reserved key', detail: `${key} is a built-in transport mode`, life: 3000 });
      return;
    }
    const inherit = {
      transport_mode: inheritFrom?.transport_mode || 'passenger',
      lane_type: inheritFrom?.lane_type || 'sentri',
    };
    const updated = [...crossingSections];
    const section = updated[sectionIdx];
    const list = [...(section.customTransportModes || [])];
    const editingKey = customTransportDialog.editingKey;
    const duplicate = list.some((item) => item.key === key && item.key !== editingKey);
    if (duplicate) {
      toast.current?.show({ severity: 'warn', summary: 'Already exists', detail: `Custom transport "${key}" is already on this crossing`, life: 3000 });
      return;
    }
    const openDays = normalizeOpenDays(synthetic?.openDays);
    if (!openDays.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Open days required',
        detail: 'Select at least one day this custom transport is open',
        life: 3000,
      });
      return;
    }
    const next = {
      key,
      labels: { ...emptyLangLabels(), ...(labels || {}) },
      icon: normalizeCustomTransportIcon(icon, key),
      inheritFrom: inherit,
      synthetic: {
        factorMin: Number(synthetic?.factorMin) || 0.65,
        factorMax: Number(synthetic?.factorMax) || 1.05,
        openDays,
        ranges: pruneRangesToOpenDays(
          Array.isArray(synthetic?.ranges) ? synthetic.ranges : defaultSyntheticRanges(openDays),
          openDays
        ),
      },
    };
    if (editingKey) {
      const idx = list.findIndex((item) => item.key === editingKey);
      if (idx >= 0) list[idx] = next;
      else list.push(next);
    } else {
      list.push(next);
    }
    (section.ports || []).forEach((port) => {
      if (!port.visibility || typeof port.visibility !== 'object') port.visibility = {};
      if (!port.sources || typeof port.sources !== 'object') port.sources = {};
      const prevKey = editingKey && editingKey !== key ? editingKey : null;
      const alreadyOn = Array.isArray(port.visibility[key]) && port.visibility[key].length > 0;
      const wasOn = prevKey && Array.isArray(port.visibility[prevKey]) && port.visibility[prevKey].length > 0;

      if (!editingKey) {
        // New custom transport: turn on for every port so synthetic waits + UI appear immediately.
        port.visibility[key] = [inherit.lane_type];
        port.sources[key] = ['synthetic'];
      } else if (alreadyOn || wasOn) {
        port.visibility[key] = [inherit.lane_type];
        const prevSources = Array.isArray(port.sources[key]) && port.sources[key].length
          ? port.sources[key]
          : (prevKey && Array.isArray(port.sources[prevKey]) ? port.sources[prevKey] : ['synthetic']);
        port.sources[key] = prevSources.includes('synthetic')
          ? [...prevSources]
          : [...prevSources, 'synthetic'];
      }
      if (prevKey) {
        delete port.visibility[prevKey];
        delete port.sources[prevKey];
      }
    });
    section.customTransportModes = list;
    setCrossingSections(updated);
    closeCustomTransportDialog();
  };

  const removeCustomTransport = (sectionIdx, transportKey) => {
    const updated = [...crossingSections];
    const section = updated[sectionIdx];
    section.customTransportModes = (section.customTransportModes || []).filter((item) => item.key !== transportKey);
    (section.ports || []).forEach((port) => {
      if (port.visibility) delete port.visibility[transportKey];
      if (port.sources) delete port.sources[transportKey];
    });
    setCrossingSections(updated);
  };

  const confirmRemoveCustomTransport = (sectionIdx, transportKey) => {
    confirmDialog({
      header: 'Remove custom transport',
      message: `Remove "${transportKey}"? This also clears it from the ports on this crossing.`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      accept: () => removeCustomTransport(sectionIdx, transportKey),
    });
  };

  // const addMetaTag = () => {
  //   setMetaTags([...metaTags, { name: "", content: "" }]);
  // };
  const addMetaTag = () => {
    setMetaTags([...metaTags, { attributes: [{ key: '', value: '' }] }]);
  };

  const removeMetaTag = (tagIndex) => {
    if (metaTags.length > 1) {
      setMetaTags(metaTags.filter((_, i) => i !== tagIndex));
    }
  };

  // const removeMetaTag = (metaIndex) => {
  //   setMetaTags(metaTags.filter((_, i) => i !== metaIndex));
  // };

  // const addAttribute = (metaIndex) => {
  //   const updated = [...metaTags];
  //   updated[metaIndex] = { ...updated[metaIndex], [""]: "" };
  //   setMetaTags(updated);
  // };

  const addAttribute = (tagIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes.push({ key: '', value: '' });
    setMetaTags(updated);
  };

  // const removeAttribute = (metaIndex, attr) => {
  //   const updated = [...metaTags];
  //   delete updated[metaIndex][attr];
  //   setMetaTags(updated);
  // };

  const removeAttribute = (tagIndex, attrIndex) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes = updated[tagIndex].attributes.filter((_, i) => i !== attrIndex);
    setMetaTags(updated);
  };

  const updateAttribute = (
    tagIndex,
    attrIndex,
    field,
    newValue
  ) => {
    const updated = [...metaTags];
    updated[tagIndex].attributes[attrIndex][field] = newValue;
    setMetaTags(updated);
  };

  const updateMainAttribute = (
    tagIndex,
    // attrIndex,
    field,
    newValue
  ) => {
    const updated = [...metaTags];
    updated[tagIndex][field] = newValue;
    setMetaTags(updated);
  };

  const updateTagType = (
    tagIndex,
    type
  ) => {

    console.log('Updating tag type:', tagIndex, type);

    const updated = [...metaTags];
    updated[tagIndex].type = type;
    setMetaTags(updated);
  };


  // const updateAttribute = (metaIndex, attr, value, isAttrName = false) => {
  //   const updated = [...metaTags];
  //   if (isAttrName) {
  //     // Cambiar nombre del atributo
  //     const oldValue = updated[metaIndex][attr];
  //     delete updated[metaIndex][attr];
  //     updated[metaIndex][value] = oldValue;
  //   } else {
  //     // Cambiar valor del atributo
  //     updated[metaIndex][attr] = value;
  //   }
  //   setMetaTags(updated);
  // };

  // const BORDER_OPTIONS = [
  //   { code: 'ca', name: 'Canadian Border' },
  //   { code: 'mexico', name: 'Mexican Border' },
  // ];

  // react-hook-form
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      _id: '',
      // pageType: { code: 'entryPage', name: 'Entry page' },
      lang: '',
      border: '',
      portName: '',
      portCrossingName: '',
      portNumber: '',
      slug: '',
      full_slug: '',
      url: '',
      h1: '',
      h2: '',
      description: '',
      crossing_names_section: {
        name: '',
        description: '',
        items: []
      },
      image: {
        url: '',
        alt: '',
        title: '',
        width: '',
        height: '',
        caption: ''
      },
      faqs_section: {
        h3: '',
        p: '',
        items: []
      },
      title: '',
      revalidated_at: '',
      state: '',
      city: '',
      translations: [],
      terminology: {
        preset: 'garita',
        singular: 'garita',
        plural: 'garitas',
        gender: 'f',
      },
      // lanes: [],
      // ports: [],
      // entry_ports_section: {
      //   h3: '',
      //   p: '',
      //   items: []
      // }
    }
  });

  // Watchers for dynamic fields
  const watchLang = watch('lang');
  const watchDepartureBorder = watch('departure.border');
  const watchSlug = watch('slug');
  const watchPortName = watch('portName');
  const watchFullSlug = watch('full_slug');
  const watchTranslations = watch('translations');
  const watchTerminologyPreset = watch('terminology.preset');
  const formSnapshot = watch();

  // Estado para el porcentaje
  const [completionPercentage, setCompletionPercentage] = useState(0);

  const calculateCompletionPercentage = useCallback(() => {
    const values = getValues();

    const fieldChecks = [
      () => values.lang?.code?.trim(),
      () => values.border?.code?.trim(),
      () => values.state?.code?.trim(),
      () => values.city?.code?.trim(),
      () => values.slug?.trim(),
      () => values.crossing_names_section?.name?.trim(),
      () => values.crossing_names_section?.description?.trim(),
      () => crossingSections.length > 0,
      () => metaTags.some(tag =>
        tag.attributes?.some(attr => attr.key?.trim() && attr.value?.trim()) ||
        tag.content?.trim() ||
        tag.name?.trim()
      ),
      () => values.image?.url?.trim()
    ];

    const filledFields = fieldChecks.filter(check => check()).length;
    return Math.round((filledFields / fieldChecks.length) * 100);
  }, [getValues, crossingSections, metaTags]);

  // Effect para actualizar el porcentaje cuando cambien los datos
  useEffect(() => {
    const percentage = calculateCompletionPercentage();
    setCompletionPercentage(percentage);
  }, [formSnapshot, crossingSections, metaTags, calculateCompletionPercentage]);

  const public_url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';


  const convertToMetaTagsFormat = (tags) => {
    return tags?.map(tag => {
      const attributesArray = Object.entries(tag.attributes || {}).map(([key, value]) => ({
        key: key.trim(),
        value: value.toString().trim()
      }));

      const typeTag = tagOptions.find(option => option.code === tag.type) || {};

      return {
        type: typeTag || {},
        content: tag.content || '',
        name: tag.name || '',
        attributes: attributesArray
      };
    });
  };


  // Fetch route data by id
  useEffect(() => {
    if (!id) return;
    api.get('/entry_pages/' + id)
      .then(response => {
        const data = response.data;
        // console.log('Fetched data?.crossing_names_section?.crossings_names:', data?.crossing_names_section?.crossings_names);
        setCrossingSections(data?.crossing_names_section?.crossings_names || []);

        const oldTags = convertToMetaTagsFormat(data?.tags || []);
        const selectedLang = LANG_OPTIONS.find(l => l.code === data.lang) || '';


        const faqs_section = {
          h3: data.faqs_section?.h3 || '',
          p: data.faqs_section?.p || '',
          items: data.faqs_section?.items?.map(faq => ({
            question: faq.question || '',
            answer: faq.answer || '',
            answer_html: faq.answer_html || '',
            answer_html_updated: faq.answer_html || ''
          })) || []
        };

        // .map(faq => ({
        //   question: faq.question || '',
        //   answer: faq.answer || '',
        //   answer_html: faq.answer_html || '',
        //   answer_html_updated: faq.answer_html_updated || ''
        // })) || { h3: '', p: '', items: [] };

        // console.log('selectedLang', selectedLang)

        setMetaTags(oldTags || []);
        reset({
          _id: data._id,
          pageType: { code: 'entryPage', name: 'Entry page' },
          lang: selectedLang,
          border: BORDER_OPTIONS.find(b => b.code === data.border) || '',
          state: BORDER_STATES.find(s => s.code === data.state) || '',
          city: BORDER_CITIES_BY_STATE[data.state]?.find(c => c.code === data.city) || '',
          departure: {
            border: BORDER_OPTIONS.find(b => b.code === data.departure?.border) || '',
            state: BORDER_STATES.find(s => s.code === data.departure?.state) || '',
            city: BORDER_CITIES_BY_STATE[data.departure?.state]?.find(c => c.code === data.departure?.city) || '',
          },
          arrival: {
            border: BORDER_OPTIONS.find(b => b.code === data.arrival?.border) || '',
            state: BORDER_STATES.find(s => s.code === data.arrival?.state) || '',
            city: BORDER_CITIES_BY_STATE[data.arrival?.state]?.find(c => c.code === data.arrival?.city) || '',
          },
          portName: data.port_name ?? '',
          portCrossingName: data.crossing_name ?? '',
          crossing_names_section: data.crossing_names_section ?? {},
          portNumber: data.port_number ?? '',
          slug: data.slug ?? '',
          full_slug: data.full_slug ?? '',
          url: data.url ?? '',
          h1: data.h1 ?? '',
          h2: data.h2 ?? '',
          description: data.description ?? '',
          image: data.image || {
            url: '',
            alt: '',
            title: '',
            width: '',
            height: '',
            caption: ''
          },
          faqs_section: faqs_section,
          title: data.title,
          revalidated_at: data.revalidated_at ?? '',
          revalidated: data.revalidated ?? false,
          lanes: data.lanes || [],
          ports: data.ports || [],
          translations: data.translations || [],
          terminology: resolveTerminology(data.terminology),
        });
      })
      .catch(error => {
        if (error.response?.status === 401) {
          console.log('Session expired, redirecting...');
        }
      });
    // eslint-disable-next-line
  }, [id]);

  // Update URL and full_slug when relevant fields change
  useEffect(() => {
    const langSegment = (watchLang?.code || '').toLowerCase(); // Agregar .toLowerCase() aquí
    const borderSegment = borderPathSegment(watchDepartureBorder?.code || '');
    const slugSegment = watchSlug?.trim() || '';
    let full_slug = `/${langSegment}`;

    if (borderSegment) full_slug += `/${borderSegment}`;
    if (slugSegment) full_slug += `/${slugSegment}`;

    full_slug = full_slug.replace(/\/{2,}/g, '/');

    setValue('full_slug', full_slug);
    setValue('url', `https://bordergarita.admin${full_slug}`);
  }, [watchLang, watchDepartureBorder, watchSlug, setValue]);

  // Fetch port options when border changes
  useEffect(() => {
    if (!watchDepartureBorder) return;
    setIsLoadingPorts(true);
    api.get('/crossing_names/border/' + watchDepartureBorder.name)
      .then(response => setPortOptions(response.data))
      .catch(() => setPortOptions([]))
      .finally(() => setIsLoadingPorts(false));
  }, [watchDepartureBorder]);

  useEffect(() => {
    setIsLoadingPorts(true);
    api.get('/crossing_names/')
      .then(response => setManualPortOptions(response.data))
      .catch(() => setManualPortOptions([]))
      .finally(() => setIsLoadingPorts(false));
  }, [watchDepartureBorder]);


  // Fetch crossing names when portName changes
  useEffect(() => {
    if (!watchPortName) return;
    setLoadingCrossingNames(true);
    const selectedPort = portOptions.find(port => port._id === watchPortName);
    api.get(`/crossing_names/port_name/${selectedPort?.port_name || watchPortName}`)
      .then(response => {

        const port_content = response.data.map(item => ({
          // ...item,
          port_number: item.port_number || '',
          h2: "",
          h3: ""
        }));

        console.log('Port content:', port_content);

        setValue('entry_ports_section.items', port_content);

        setCrossingNames(response.data)
      })
      .catch(() => setCrossingNames([]))
      .finally(() => setTimeout(() => setLoadingCrossingNames(false), 1000));
  }, [watchPortName, portOptions, setValue]);

  // FAQ handlers
  const handleAddFaq = () => {
    console.log('Adding new FAQ');
    const faqs = getValues('faqs_section.items') || [];
    console.log('Current FAQs:', faqs);
    console.log('array', getValues('faqs_section.items')?.map((_, idx) => idx))
    setValue('faqs_section.items', [...faqs, { question: '', answer: '' }]);
    trigger('faqs_section.items');
  };

  const handleRemoveFaq = (idx) => {
    const faqs = getValues('faqs_section.items') || [];
    setValue('faqs_section.items', faqs.filter((_, i) => i !== idx));
    trigger('faqs_section.items');
  };
  const handleFaqChange = (idx, field, value) => {
    const faqs = getValues('faqs_section.items') || [];
    faqs[idx][field] = value;
    setValue('faqs_section.items', [...faqs]);
    trigger('faqs_section.items');
  };

  // Handlers para traducciones
  const handleAddTranslation = () => {
    const currentTranslations = getValues('translations') || [];
    setValue('translations', [...currentTranslations, { lang: '', entry_page_id: '' }]);
    trigger('translations');
  };

  const handleRemoveTranslation = (index) => {
    const currentTranslations = getValues('translations') || [];
    setValue('translations', currentTranslations.filter((_, i) => i !== index));
    trigger('translations');
  };

  // Save handler
  const onSave = async (data) => {
    console.log('Form data:', data);
    console.log('crossingSections data:', crossingSections);
    const payload = {
      ...data,
      image: {
        url: data.image?.url || '',
        alt: data.image?.alt || '',
        title: data.image?.title || '',
        caption: data.image?.caption || '',
        // width: data.image?.width || '',
        // height: data.image?.height || '',
        credits: {
          source: {
            url: data.image?.credits?.source?.url || '',
            text: data.image?.credits?.source?.text || ''
          },
          author: {
            url: data.image?.credits?.author?.url || '',
            text: data.image?.credits?.author?.text || ''
          }
        }
      },
      departure: {
        border: data.departure?.border?.code || '',
        state: data.departure?.state?.code || '',
        city: data.departure?.city?.code || '',
      },
      arrival: {
        border: data.arrival?.border?.code || '',
        state: data.arrival?.state?.code || '',
        city: data.arrival?.city?.code || '',
      },
      crossing_names_section: {
        name: data.crossing_names_section.name,
        description: data.crossing_names_section.description,
        crossings_names: crossingSections.map((section) => ({
          ...section,
          customTransportModes: section.customTransportModes || [],
          ports: (section.ports || []).map((port) => {
            const visibility = { ...(port.visibility || {}) };
            const sources = { ...(port.sources || {}) };
            modesForSection(section).forEach((mode) => {
              const key = mode.transport_mode;
              const lanes = Array.isArray(visibility[key]) ? visibility[key] : [];
              if (!lanes.length) {
                sources[key] = [];
                return;
              }
              if (mode.custom) {
                visibility[key] = [mode.inheritFrom?.lane_type || lanes[0] || 'sentri'];
              }
              if (!Array.isArray(sources[key])) {
                sources[key] = [...mode.defaultSources];
              }
            });
            return {
              ...port,
              port_name: port.port_name || '',
              port_number: port.port_number || '',
              visibility,
              sources,
            };
          }),
        })),
      },
      tags: getFinalMetaTags,
      faqs_section: {
        h3: data.faqs_section.h3 || '',
        p: data.faqs_section.p || '',
        items: data.faqs_section.items.map(faq => ({
          question: faq.question || '',
          answer: faq.answer || '',
          answer_html: faq.answer_html_updated || faq.answer_html || ''
        })),
      },
      lang: data.lang?.code || 'en',
      border: data.border?.code || '',
      port_name: data.portName || '',
      url: data.full_slug || '',
      state: data.state?.code || '',
      city: data.city?.code || '',
      translations: data.translations || [],
      terminology: resolveTerminology(data.terminology),
      // ports: crossingNames.map(name => name.port_number) || [],
    };

    console.log('getFinalMetaTags:', getFinalMetaTags);

    if (!getFinalMetaTags[0]?.type) {
      delete payload.tags
    }

    delete payload._id

    console.log('Payload to save:', payload);

    try {
      setIsLoadingEntryPage(true);
      if (id) {
        await api.put(`/entry_pages/${id}`, payload);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Page updated', life: 3000 });
      } else {
        await api.post('/entry_pages', payload).then((response) => {
          console.log('created response...', response.data._id)
          navigate(`/entry_page/edit/${response.data._id}`);
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Page created', life: 3000 });
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingEntryPage(false);
    }
  };

  // Go back
  const handleGoBack = () => window.history.back();

  const openWidget = () => {
    window.cloudinary.openUploadWidget(
      {
        cloudName: 'dtxar5b36',
        uploadPreset: 'bordergarita_uploads'
      },
      (error, result) => {
        if (!error && result.event === 'success') {
          const imageUrl = result.info.secure_url;
          console.log('URL:', imageUrl);
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Image uploaded', life: 3000 });
          setValue('image.url', imageUrl, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          });

          // Opcional: Si aún no se actualiza, usa watch para forzar la reactividad
          trigger('image');
        }
      }
    );
  };

  const watchedImageUrl = watch('image');

  const [isRevalidateLoading, setIsRevalidateLoading] = useState(false);

  const onRevalidateUrl = async () => {
    const path = getValues('full_slug')
    const url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET

    setIsRevalidateLoading(true)
    fetch(`${url}/api/revalidate?secret=${secret}&path=${path}`, {
      method: 'POST',
    })
      .then(res => res.json())
      .then(data => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'URL revalidated successfully', life: 3000 });
      })
      .catch(err => console.error(err))
      .finally(() => {
        setIsRevalidateLoading(false);
      });
  }

  const isValidUrl = useMemo(() => {
    const langSegment = watchLang?.code || 'en';
    const borderSegment = watchDepartureBorder?.code || '';
    const slugSegment = watchSlug?.trim() || '';
    if (langSegment && borderSegment && slugSegment) {
      return true;
    }
  }, [watchLang, watchDepartureBorder, watchSlug]);

  const [revalidationLoading, setRevalidationLoading] = useState(false);

  const removeCrossingSection = (index) => {
    setCrossingSections((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleFirstRevalidation = async () => {
    setRevalidationLoading(true);
    console.log('Revalidating URL...');
    setTimeout(() => {
      const payload = {
        revalidated: true,
      }

      if (id) {
        api.put(`/entry_pages/${id}`, payload).then(res => {
          console.log('Revalidation response:', res);
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Entry page published successfully', life: 3000 });
          setValue('revalidated', true);
          trigger('revalidated');
          window.open(`${process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL}/${res.data.full_slug}`, "_blank", "width=450,height=700");
          // console.log('res', res)
          // const userConsent = confirm("Entry page published successfully. The page will open in a new tab");
          // if (userConsent) {
          //   // window.open("https://example.com", "_blank", );
          // }
          // alert('');
        }).finally(() => {
          setRevalidationLoading(false);
        });
      }
      console.log('Payload for revalidation:', payload);

    }, 2000);
  }

  const getFinalMetaTags = useMemo(() => {
    return metaTags?.map((tag, index) => {
      // Crear objeto base con las propiedades fijas

      const finalTag = {
        type: tag?.type?.code,
        name: tag.name || '',
        self_closing: tag?.type?.self_closing || false,
        content: tag?.content || '',
        attributes: {}
      };

      // Procesar los atributos
      if (tag.attributes && Array.isArray(tag.attributes)) {
        tag.attributes.forEach(attr => {
          if (attr.key && attr.key.trim() && attr.value !== undefined) {
            finalTag.attributes[attr.key.trim()] = attr.value.toString().trim();
          }
        });
      }

      return finalTag;
    });
  }, [metaTags]);

  function renderAnswer(answer, idx) {
    if (!answer) {
      return <em>No answer for question {idx + 1}</em>;
    }

    // Regex para detectar URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Dividimos el texto en fragmentos
    const parts = answer.split(urlRegex);

    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        return (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>
            {part}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  }

  const selectedDepartureBorder = watch('departure.border');
  const selectedArrivalBorder = watch('arrival.border');
           
  const selectedDepatureState = watch('departure.state');
  const selectedArrivalState = watch('arrival.state');


  const [departureStateOptions, setDepartureStateOptions] = useState(BORDER_STATES);
  const [arrivalStateOptions, setArrivalStateOptions] = useState(BORDER_STATES);

  const [departureCities, setDepartureCities] = useState([]);
  const [arrivalCities, setArrivalCities] = useState([]);

  useEffect(() => {
    console.log('Selected departure border changed:', selectedDepartureBorder);
    if (selectedDepartureBorder) {
      const filteredStates = BORDER_STATES.filter((state) => state.border === selectedDepartureBorder.code);
      setDepartureStateOptions(filteredStates);
    } else {
      // setDepartureStateOptions(BORDER_STATES);
      setValue('departure.state', '');
    }
  }, [selectedDepartureBorder, setValue]);

  useEffect(() => {
    console.log('Selected arrival border changed:', selectedArrivalBorder);
    if (selectedArrivalBorder) {
      const filteredStates = BORDER_STATES.filter((state) => state.border === selectedArrivalBorder.code);
      setArrivalStateOptions(filteredStates);
    } else {
      // setArrivalStateOptions(BORDER_STATES);
      setValue('arrival.state', '');
    }
  }, [selectedArrivalBorder, setValue]);

  useEffect(() => {
    console.log('Selected departure state changed:', selectedDepatureState);
    // console.log('Updating cities for state:', BORDER_CITIES_BY_STATE[selectedDepatureState?.code]);
    if (selectedDepatureState && BORDER_CITIES_BY_STATE[selectedDepatureState.code]) {
      setDepartureCities(BORDER_CITIES_BY_STATE[selectedDepatureState.code]);
    } else {
      // setDepartureCities([]);
      setValue('departure.city', ''); // Limpiar ciudad cuando cambia el estado
    }
  }, [selectedDepatureState, setValue]);

  useEffect(() => {
    // console.log('Selected state changed:', selectedArrivalState);
    // console.log('Updating cities for state:', BORDER_CITIES_BY_STATE[selectedArrivalState?.code]);
    if (selectedArrivalState && BORDER_CITIES_BY_STATE[selectedArrivalState.code]) {
      setArrivalCities(BORDER_CITIES_BY_STATE[selectedArrivalState.code]);
    } else {
      // setArrivalCities([]);
      setValue('arrival.city', ''); // Limpiar ciudad cuando cambia el estado
    }
  }, [selectedArrivalState, setValue]);

  const formatTextForTextarea = (text) => {
    return text?.replace(/\n/g, '\n')?.replace(/\t/g, '\t');
  };

  const getPort = (sectionIdx, portIdx) =>
    crossingSections[sectionIdx]?.ports?.[portIdx];

  const modeConfigFor = (transportMode, sectionIdx) =>
    modesForSection(crossingSections[sectionIdx]).find((m) => m.transport_mode === transportMode);

  const defaultSourcesFor = (transportMode, sectionIdx) =>
    modeConfigFor(transportMode, sectionIdx)?.defaultSources || ['community'];

  const isModeActive = (sectionIdx, portIdx, transportMode) =>
    (getPort(sectionIdx, portIdx)?.visibility?.[transportMode] || []).length > 0;

  const isLaneActive = (sectionIdx, portIdx, transportMode, laneType) =>
    Boolean(getPort(sectionIdx, portIdx)?.visibility?.[transportMode]?.includes(laneType));

  const resolvedSources = (sectionIdx, portIdx, transportMode) => {
    const port = getPort(sectionIdx, portIdx);
    if (!port) return [];
    if (!isModeActive(sectionIdx, portIdx, transportMode)) return [];
    const saved = port.sources?.[transportMode];
    if (Array.isArray(saved)) return saved;
    return defaultSourcesFor(transportMode, sectionIdx);
  };

  const isSourceActive = (sectionIdx, portIdx, transportMode, source) =>
    resolvedSources(sectionIdx, portIdx, transportMode).includes(source);

  const toggleTransportMode = (sectionIdx, portIdx, transportMode, checked) => {
    const newSections = [...crossingSections];
    const port = newSections[sectionIdx].ports[portIdx];
    if (!port.visibility) port.visibility = {};
    if (!port.sources) port.sources = {};

    if (checked) {
      const mode = modeConfigFor(transportMode, sectionIdx);
      const inheritLane = mode?.inheritFrom?.lane_type || mode?.lane_types?.[0]?.type;
      const allLanes = mode?.custom && inheritLane
        ? [inheritLane]
        : (mode?.lane_types.map((t) => t.type) || []);
      port.visibility[transportMode] = allLanes;
      port.sources[transportMode] = [...defaultSourcesFor(transportMode, sectionIdx)];
    } else {
      port.visibility[transportMode] = [];
      port.sources[transportMode] = [];
    }

    setCrossingSections(newSections);
  };

  const toggleLaneType = (sectionIdx, portIdx, transportMode, laneType, checked) => {
    const newSections = [...crossingSections];
    const port = newSections[sectionIdx].ports[portIdx];
    if (!port.visibility) port.visibility = {};
    if (!port.visibility[transportMode]) port.visibility[transportMode] = [];

    if (checked) {
      if (!port.visibility[transportMode].includes(laneType)) {
        port.visibility[transportMode].push(laneType);
      }
    } else {
      port.visibility[transportMode] = port.visibility[transportMode].filter((t) => t !== laneType);
      if (!port.visibility[transportMode].length && port.sources) {
        port.sources[transportMode] = [];
      }
    }

    setCrossingSections(newSections);
  };

  const toggleSource = (sectionIdx, portIdx, transportMode, source, checked) => {
    const newSections = [...crossingSections];
    const port = newSections[sectionIdx].ports[portIdx];
    if (!port.visibility) port.visibility = {};
    if (!port.sources) port.sources = {};

    const current = Array.isArray(port.sources[transportMode])
      ? [...port.sources[transportMode]]
      : isModeActive(sectionIdx, portIdx, transportMode)
        ? [...defaultSourcesFor(transportMode, sectionIdx)]
        : [];

    const next = checked
      ? current.includes(source) ? current : [...current, source]
      : current.filter((s) => s !== source);

    if (next.length === 0) {
      port.visibility[transportMode] = [];
      port.sources[transportMode] = [];
    } else {
      if (!(port.visibility[transportMode] || []).length) {
        port.visibility[transportMode] =
          modeConfigFor(transportMode, sectionIdx)?.lane_types.map((t) => t.type) || [];
      }
      port.sources[transportMode] = next;
    }

    setCrossingSections(newSections);
  };


  return (
    <div className="grid">
      <Toast ref={toast} />
      <ConfirmDialog />
      <CustomTransportDialog
        visible={customTransportDialog.visible}
        editingKey={customTransportDialog.editingKey}
        initialKey={customTransportDialog.initialKey}
        initialLabels={customTransportDialog.initialLabels}
        initialIcon={customTransportDialog.initialIcon}
        initialInheritFrom={customTransportDialog.initialInheritFrom}
        initialSynthetic={customTransportDialog.initialSynthetic}
        onHide={closeCustomTransportDialog}
        onSave={saveCustomTransport}
      />
      <form className="space-y-4" onSubmit={handleSubmit(onSave)}>
        <div className='col-12 md:col-12 lg:col-12'>
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.1px' }}>
            <div style={{ alignSelf: 'flex-start' }}>
              <Button icon="pi pi-arrow-left" className="p-button-text p-button-sm" onClick={handleGoBack} style={{ padding: '0.5rem 0.5rem', fontSize: '1rem' }} aria-label="Go Back" />
            </div>
            <Card title={
              <strong>
                {getValues('_id') ? `Edit ${getValues('full_slug')}` : 'New Entry Page'}
              </strong>} className='w-full'>
              <CompletionIndicator percentage={completionPercentage} />
              <>
                <div className="col-12">
                  <Panel header="Configure entry page" >
                    <div className="grid">
                      <div className="col-12 md:col-4">
                        <label><strong>Page type</strong></label>
                        <Controller
                          name="pageType"
                          control={control}
                          // disabled={!!getValues('_id')}
                          rules={{ required: "Page type is required" }}
                          render={({ field }) => (
                            <Dropdown {...field} options={PAGE_TYPES} optionDisabled={option => option.disabled} optionLabel="name" placeholder="Select a page type" className="w-full" />
                          )}
                        />
                      </div>
                      <div className="col-12 md:col-4">
                        <label><strong>Language</strong></label>
                        <Controller
                          name="lang"
                          // disabled={!!getValues('_id')}
                          control={control}
                          render={({ field }) => (
                            <Dropdown {...field} options={LANG_OPTIONS} optionDisabled="disabled" optionLabel="name" placeholder="Select a language" className="w-full" />
                          )}
                        />
                      </div>
                      <div className="col-12 md:col-4">
                        <label><strong>Terminology</strong></label>
                        <Controller
                          name="terminology.preset"
                          control={control}
                          render={({ field }) => (
                            <Dropdown
                              value={TERMINOLOGY_OPTIONS.find((o) => o.code === field.value) || TERMINOLOGY_OPTIONS[0]}
                              options={TERMINOLOGY_OPTIONS}
                              optionLabel="name"
                              placeholder="Select terminology"
                              className="w-full"
                              onChange={(e) => {
                                const option = e.value;
                                const code = option?.code || 'garita';
                                field.onChange(code);
                                if (code === 'custom') {
                                  setValue('terminology.singular', getValues('terminology.singular') || '');
                                  setValue('terminology.plural', getValues('terminology.plural') || '');
                                  setValue('terminology.gender', getValues('terminology.gender') === 'm' ? 'm' : 'f');
                                } else {
                                  setValue('terminology.singular', option?.singular || 'garita');
                                  setValue('terminology.plural', option?.plural || 'garitas');
                                  setValue('terminology.gender', option?.gender || 'f');
                                }
                                setValue('terminology.preset', code);
                              }}
                            />
                          )}
                        />
                      </div>
                      {watchTerminologyPreset === 'custom' && (
                        <>
                          <div className="col-12 md:col-4">
                            <label><strong>Singular</strong></label>
                            <Controller
                              name="terminology.singular"
                              control={control}
                              render={({ field }) => (
                                <InputText {...field} className="w-full" placeholder="e.g. puente" />
                              )}
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <label><strong>Plural</strong></label>
                            <Controller
                              name="terminology.plural"
                              control={control}
                              render={({ field }) => (
                                <InputText {...field} className="w-full" placeholder="e.g. puentes" />
                              )}
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <label><strong>Artículo</strong></label>
                            <Controller
                              name="terminology.gender"
                              control={control}
                              render={({ field }) => (
                                <Dropdown
                                  value={TERMINOLOGY_GENDER_OPTIONS.find((o) => o.code === field.value) || TERMINOLOGY_GENDER_OPTIONS[0]}
                                  options={TERMINOLOGY_GENDER_OPTIONS}
                                  optionLabel="name"
                                  placeholder="Femenino o masculino"
                                  className="w-full"
                                  onChange={(e) => field.onChange(e.value?.code || 'f')}
                                />
                              )}
                            />
                          </div>
                        </>
                      )}
                      
                      <div className="col-12 md:col-12">
                        <div className="grid">
                          <div className="col-12 text-primary">
                            <span className="text-sm font-bold uppercase tracking-wider">Departure Location</span>
                            <i className="pi pi-arrow-up-right ml-2 font-bold"></i>
                          </div>
                          <div className="col-4 md:col-4">
                          <label><strong>Departure border</strong></label>
                          <Controller
                            name="departure.border"
                            control={control}
                            render={({ field }) => (
                              <Dropdown {...field} options={BORDER_OPTIONS.filter((border) => border.type === 'departure')} optionDisabled="disabled" optionLabel="name" placeholder="Select a border" className="w-full" />
                            )}
                          />
                        </div>

                        <div className="col-4 md:col-4">
                          <label><strong>Departure state</strong></label>
                          <Controller
                            name="departure.state"
                            control={control}
                            filter
                            filterInputAutoFocus
                            filterBy="name"
                            render={({ field }) => (
                              <Dropdown
                                {...field}
                                filter
                                filterBy="name"
                                filterInputAutoFocus
                                options={departureStateOptions}
                                optionLabel="name"
                                placeholder="Select a state"
                                className="w-full"
                              />
                            )}
                          />
                        </div>

                        <div className="col-4 md:col-4">
                          <label><strong>Departure city</strong></label>
                          <Controller
                            name="departure.city"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                {...field}
                                options={departureCities}
                                optionLabel="name"
                                filter
                                filterBy="name"
                                filterInputAutoFocus
                                placeholder="Select a city"
                                className="w-full"
                              // disabled={departureCities.length === 0}
                              />
                            )}
                          />
                        </div>
                        </div>
                      </div>
                      <div className="col-12 md:col-12">
                        <div className="grid">
                          <div className="col-12 text-primary">
                            <span className="text-sm font-bold uppercase tracking-wider">Arrival Location</span>
                            <i className="pi pi-arrow-down-left ml-2 font-bold"></i>
                          </div>
                          <div className="col-4 md:col-4">
                          <label><strong>Arrival border</strong></label>
                          <Controller
                            name="arrival.border"
                            // disabled={!!getValues('_id')}
                            control={control}
                            render={({ field }) => (
                              <Dropdown {...field} options={BORDER_OPTIONS.filter((border) => border.type === 'arrival')} optionDisabled="disabled" optionLabel="name" placeholder="Select a border" className="w-full" />
                            )}
                          />
                        </div>

                        <div className="col-4 md:col-4">
                          <label><strong>Arrival state</strong></label>
                          <Controller
                            name="arrival.state"
                            control={control}
                            filter
                            filterInputAutoFocus
                            filterBy="name"
                            render={({ field }) => (
                              <Dropdown
                                {...field}
                                filter
                                filterBy="name"
                                filterInputAutoFocus
                                options={arrivalStateOptions}
                                optionLabel="name"
                                placeholder="Select a state"
                                className="w-full"
                              />
                            )}
                          />
                        </div>

                        <div className="col-4 md:col-4">
                          <label><strong>Arrival city</strong></label>
                          <Controller
                            name="arrival.city"
                            control={control}
                            render={({ field }) => (
                              <Dropdown
                                {...field}
                                options={arrivalCities}
                                filter
                                filterBy="name"
                                filterInputAutoFocus
                                optionLabel="name"
                                placeholder="Select a city"
                                className="w-full"
                              // disabled={arrivalCities.length === 0}
                              />
                            )}
                          />
                        </div>
                        </div>
                      </div>

                      <div className="col-12 md:col-12">
                        <label><strong>Path</strong> <span style={{ fontSize: '0.85rem', color: '#888', marginLeft: '0.25rem' }}>(/url-end)</span></label>
                        <Controller
                          name="slug"
                          control={control}
                          disabled={!!getValues('_id')}
                          render={({ field }) => (
                            <InputText {...field} className="w-full" placeholder="Port crossing name" style={{ fontSize: '0.95rem' }} />
                          )}
                        />
                      </div>
                      <div className="col-12 md:col-12">
                        <label>
                          <strong>Full path
                            <span style={{ fontSize: '0.85rem', color: '#888', marginLeft: '0.25rem' }}>(autogeneratged)</span>
                          </strong>
                        </label>
                        <Controller
                          name="full_slug"
                          control={control}
                          render={({ field }) => (
                            <InputText {...field} disabled className="w-full" placeholder="Full path" style={{ fontSize: '0.95rem' }} />
                          )}
                        />
                      </div>
                    </div>

                    {
                      !getValues('_id') && !getValues('revalidated') && !getValues("revalidated_at") && (
                        <div className='grid'>
                          <div className='col-8'>
                            <div>
                              {public_url}{watchFullSlug}
                            </div>
                          </div>
                          <div className="col-4">
                            <Button
                              loading={isLoadingEntryPage}
                              label="Create entry page"
                              disabled={!isValidUrl}
                              className="main-button"
                              type="submit"
                            />
                          </div>
                        </div>
                      )
                    }
                    {
                      getValues('_id') && !getValues('revalidated') && !getValues("revalidated_at") && (
                        <div className='grid mt-2'>
                          <div className='col-8'>
                            <div className='mb-3'>
                              {
                                isValidUrl && (
                                  <Message severity="warn" text="This link is not yet available for public access yet." className="border-primary w-full justify-content-start" />
                                )
                              }
                            </div>
                            <div>
                              {public_url}{watchFullSlug}
                            </div>
                            {
                              revalidationLoading && (
                                <div className="mt-2">
                                  <ProgressBar
                                    mode="indeterminate"
                                    style={{ height: '7px', '--value-color': '#0D0D65' }}
                                  ></ProgressBar>
                                </div>
                              )
                            }
                          </div>
                          <div className="col-4">
                            <Button
                              loading={revalidationLoading}
                              onClick={() => handleFirstRevalidation()}
                              label="Publish Now"
                              tooltip="Triggers the first revalidation of this entry page's cache"
                              tooltipOptions={{ position: 'bottom' }}
                              severity="warning"
                              disabled={!isValidUrl}
                              className="p-button-primary w-full"
                              type="button"
                            />
                          </div>
                        </div>
                      )
                    }
                    {
                      getValues('_id') && getValues("revalidated") && (
                        <>
                          <div className='col-8'>
                            <Message severity="info" text="This link is available for public access." className="border-primary w-full justify-content-start" />
                          </div>
                          <div className='col-12'>
                            <a href={`${public_url}${getValues('full_slug')}`} target="_blank" rel="noopener noreferrer" className="no-underline hover:opacity-80 transition-opacity">
                              <div className="col-12 flex align-items-center">
                                <label className="text-md font-semibold mb-0 mr-2 cursor-pointer">
                                  <strong>{`${public_url}${getValues('full_slug')}`}</strong>
                                </label>
                                <i className="pi pi-external-link" style={{ fontSize: '0.9rem' }}></i>
                              </div>
                            </a>
                          </div>
                        </>
                      )
                    }

                  </Panel>
                </div>
              </>
            </Card>
            <Card className='col-12 mt-4'>
              <Panel header="Manage Translations" >
                <div className="">
                  <TranslationsManager
                    translations={watchTranslations || []}
                    onAddTranslation={handleAddTranslation}
                    onRemoveTranslation={handleRemoveTranslation}
                    control={control}
                    currentLang={watchLang?.code}
                    currentPageId={getValues('_id')}
                  />
                </div>
              </Panel>
            </Card>
            {
              getValues('revalidated') && (
                <>
                  <Card className='col-12 mt-4'>
                    <div className="grid">
                      <div className="col-8">
                        <Panel header="Configure SEO crossing names" >
                          <div className="col-12">
                            <div className="col-12 md:col-12 lg:col-12">
                              <label><strong>Header {`<h1>`}</strong></label>
                              <Controller
                                name="crossing_names_section.name"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} className="w-full" placeholder="h1 content" style={{ fontSize: '0.95rem' }} />
                                )}
                              />
                              <div className='mt-2'>
                                <label className='mt-2'><strong>Description {`<p>`}</strong></label>
                                <Controller
                                  name="crossing_names_section.description"
                                  className="mt-2"
                                  control={control}
                                  render={({ field }) => (
                                    <InputTextarea {...field} placeholder='Write your content...' style={{ height: '120px', width: '100%' }} />
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="grid">
                              {crossingSections.map((section, idx) => (
                                <div key={idx} className="col-12 md:col-12">
                                  <Card key={idx} className="shadow-2">
                                    <div className='mb-2 flex justify-content-between align-items-center'>
                                      <span className="flex align-items-start">
                                        <strong>
                                          Crossing name {idx + 1}
                                        </strong>
                                      </span>
                                      <Button
                                        type='button'
                                        icon="pi pi-trash"
                                        tooltip='Remove Crossing Name'
                                        className="p-button-danger p-button-text ml-2"
                                        onClick={() => removeCrossingSection(idx)}
                                        aria-label="Remove Crossing Name"
                                      />
                                    </div>
                                    <div className="p-fluid mb-3">
                                      <label><strong>Name in web app</strong></label>
                                      <InputText
                                        value={section.name ?? ""}
                                        onChange={(e) => updateCrossingField(idx, "name", e.target.value)}
                                      />
                                    </div>
                                    <div className="p-fluid mb-3">
                                      <label><strong>Name in Mobile apps</strong></label>
                                      <InputText
                                        value={section.mobileName ?? ""}
                                        onChange={(e) => updateCrossingField(idx, "mobileName", e.target.value)}
                                        placeholder="Shown in home and community lists"
                                      />
                                    </div>
                                    <div className="p-fluid mb-3">
                                      <label><strong>Description (h3)</strong></label>
                                      <InputText
                                        value={section.description ?? ""}
                                        onChange={(e) => updateCrossingField(idx, "description", e.target.value)}
                                      />
                                    </div>
                                    <div className="p-fluid mb-3">
                                      <label><strong>URL Fragment (HTML Id)</strong></label>
                                      <InputText
                                        style={{ marginBottom: '4px' }}
                                        value={section?.urlFragment || ""}
                                        placeholder='URL Fragment'
                                        onChange={(e) => updateCrossingField(idx, "urlFragment", e.target.value)}
                                      />
                                      <small className="text-gray-500">
                                        <strong>Example link: </strong>
                                        {`${public_url}${getValues('full_slug')}#${section.urlFragment || "no-fragment-fill"}`}
                                      </small>
                                    </div>

                                    {/* Bloque Add Port Manually */}
                                    <div
                                      className="col-12 border-round-lg shadow-2 p-4 mb-3"
                                      style={{ borderLeft: "5px solid #6366F1", minHeight: "220px" }}
                                    >
                                      <label><strong>Configure ports:</strong></label>
                                      <Dropdown
                                        options={manualPortOptions}
                                        filter
                                        showClear
                                        filterInputAutoFocus
                                        filterBy="port_name,port_number,crossing_name"
                                        placeholder="Select a CBP port"
                                        onChange={(e) => handleAddPort(idx, e.value)}
                                        optionLabel="port_name"
                                        className="w-full mt-2"
                                        itemTemplate={(option) => (
                                          <div>
                                            <span style={{ fontWeight: 500 }}>{option.port_name}</span>
                                            <span style={{ fontWeight: 500, margin: "0 6px" }}>-</span>
                                            <span style={{ fontWeight: 500 }}>{option.crossing_name}</span>
                                            <span style={{ fontWeight: 500, margin: "0 6px" }}>-</span>
                                            <span style={{ color: "#888", fontSize: "0.85em" }}>
                                              {option.port_number ? `(${option.port_number})` : ""}
                                            </span>
                                          </div>
                                        )}
                                      />

                                      {section.ports.length > 0 && (
                                        <ul className="mt-3 list-none p-0">
                                          {section.ports.map((p, portIdx) => (
                                            <li key={`${p.port_number || 'port'}-${portIdx}`} className="mb-3">
                                              <div className="flex justify-content-between align-items-start mb-2">
                                                <div className="flex flex-column">
                                                  <span><strong>{p.port_name}</strong></span>
                                                  <span>{p.port_number}</span>
                                                </div>
                                                <Button
                                                  type="button"
                                                  icon="pi pi-times"
                                                  className="p-button-text p-button-danger"
                                                  onClick={() => confirmRemovePort(idx, p)}
                                                />
                                              </div>
                                              <label className="font-semibold">Common transports</label>
                                              <div className="grid mt-2">
                                                {BUILTIN_TRANSPORT_MODES.map((mode) => {
                                                  const modeId = `${idx}-${portIdx}-${mode.transport_mode}`;
                                                  const modeOn = isModeActive(idx, portIdx, mode.transport_mode);
                                                  const cbpOn = isSourceActive(idx, portIdx, mode.transport_mode, 'cbp');
                                                  return (
                                                    <div key={mode.transport_mode} className="col-12 md:col-4">
                                                      <div className="surface-100 p-2 border-round h-full">
                                                        <div className="text-xs text-500 mb-1">Transport</div>
                                                        <div className="flex align-items-center mb-2">
                                                          <Checkbox
                                                            inputId={modeId}
                                                            onChange={(e) => toggleTransportMode(idx, portIdx, mode.transport_mode, e.checked)}
                                                            checked={modeOn}
                                                          />
                                                          <label htmlFor={modeId} className="ml-2 font-semibold uppercase text-xs">
                                                            {mode.transport_mode}
                                                          </label>
                                                        </div>
                                                        <div className="ml-4 mb-2">
                                                          <div className="text-xs text-500 mb-1">Sources</div>
                                                          {['cbp', 'community'].map((source) => (
                                                            <div key={source} className="flex align-items-center mb-1">
                                                              <Checkbox
                                                                disabled={!modeOn}
                                                                inputId={`${modeId}-${source}`}
                                                                onChange={(e) => toggleSource(idx, portIdx, mode.transport_mode, source, e.checked)}
                                                                checked={isSourceActive(idx, portIdx, mode.transport_mode, source)}
                                                              />
                                                              <label htmlFor={`${modeId}-${source}`} className="ml-2 text-sm capitalize">
                                                                {source === 'cbp' ? 'CBP' : 'Community'}
                                                              </label>
                                                            </div>
                                                          ))}
                                                          {cbpOn && !String(p.port_number || '').trim() && (
                                                            <small className="text-orange-600">Port number needed for CBP wait times</small>
                                                          )}
                                                        </div>
                                                        <div className="ml-4 flex flex-column gap-2">
                                                          <div className="text-xs text-500">Lane type</div>
                                                          {mode.lane_types.map((type) => (
                                                            <div key={type.type} className="flex align-items-center">
                                                              <Checkbox
                                                                disabled={!modeOn}
                                                                inputId={`${modeId}-${type.type}`}
                                                                onChange={(e) => toggleLaneType(idx, portIdx, mode.transport_mode, type.type, e.checked)}
                                                                checked={isLaneActive(idx, portIdx, mode.transport_mode, type.type)}
                                                              />
                                                              <label htmlFor={`${modeId}-${type.type}`} className="ml-2 text-sm capitalize">
                                                                {type.type}
                                                              </label>
                                                            </div>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      )}

                                      <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--surface-300)' }}>
                                        <div className="flex justify-content-between align-items-center">
                                          <label className="font-semibold">Custom transports</label>
                                          <Button
                                            type="button"
                                            label="Custom Transport"
                                            icon="pi pi-plus"
                                            className="p-button-outlined p-button-sm"
                                            onClick={() => openAddCustomTransport(idx)}
                                          />
                                        </div>
                                        {customModesForSection(section).length === 0 ? (
                                          <p className="text-sm text-500 mt-3 mb-0">
                                            No custom transports yet. Add one for this crossing with + Custom Transport.
                                          </p>
                                        ) : (
                                        <div className="grid mt-2">
                                          {customModesForSection(section).map((mode) => {
                                            const customLabel = formatCustomLangLabels(mode.labels);
                                            const customDef = (section.customTransportModes || []).find((c) => c.key === mode.transport_mode);
                                            const iconSrc = customTransportIconSrc(mode.icon || customDef?.icon, mode.transport_mode);
                                            return (
                                              <div key={mode.transport_mode} className="col-12 md:col-4">
                                                <div className="surface-100 p-2 border-round h-full">
                                                  <div className="flex justify-content-between align-items-start">
                                                    <div className="flex align-items-center gap-2 text-xs text-500 mb-1">
                                                      <img src={iconSrc} alt="" width={16} height={16} />
                                                      <span>Key</span>
                                                    </div>
                                                    <div className="flex">
                                                      <Button
                                                        type="button"
                                                        icon="pi pi-pencil"
                                                        className="p-button-text p-button-sm"
                                                        onClick={() => customDef && openEditCustomTransport(idx, customDef)}
                                                      />
                                                      <Button
                                                        type="button"
                                                        icon="pi pi-trash"
                                                        className="p-button-text p-button-danger p-button-sm"
                                                        onClick={() => confirmRemoveCustomTransport(idx, mode.transport_mode)}
                                                      />
                                                    </div>
                                                  </div>
                                                  {section.ports.length === 0 ? (
                                                    <>
                                                      <div className="font-semibold uppercase text-xs mb-1">{mode.transport_mode}</div>
                                                      {customLabel ? <div className="text-xs text-600 mb-1">{customLabel}</div> : null}
                                                      <small className="text-500">Add a port above to enable this transport.</small>
                                                    </>
                                                  ) : (
                                                    section.ports.map((p, portIdx) => {
                                                      const modeId = `${idx}-${portIdx}-${mode.transport_mode}`;
                                                      const modeOn = isModeActive(idx, portIdx, mode.transport_mode);
                                                      const inherit = mode.inheritFrom || defaultInheritFrom();
                                                      return (
                                                        <div key={`${p.port_number || 'port'}-${portIdx}`} className={portIdx > 0 ? 'mt-3 pt-2' : ''} style={portIdx > 0 ? { borderTop: '1px solid var(--surface-200)' } : undefined}>
                                                          {section.ports.length > 1 && (
                                                            <div className="text-xs text-500 mb-1">
                                                              {p.port_name}{p.port_number ? ` (${p.port_number})` : ''}
                                                            </div>
                                                          )}
                                                          <div className="flex align-items-center mb-2">
                                                            <Checkbox
                                                              inputId={modeId}
                                                              onChange={(e) => toggleTransportMode(idx, portIdx, mode.transport_mode, e.checked)}
                                                              checked={modeOn}
                                                            />
                                                            <label htmlFor={modeId} className="ml-2 font-semibold uppercase text-xs">
                                                              {mode.transport_mode}
                                                            </label>
                                                          </div>
                                                          {customLabel ? (
                                                            <div className="ml-4 mb-2 text-xs text-600">{customLabel}</div>
                                                          ) : null}
                                                          <div className="ml-4 mb-2 text-xs text-600">
                                                            Inherits <strong>{inherit.transport_mode}</strong> / <strong>{inherit.lane_type}</strong>
                                                            {' · Open '}
                                                            {(mode.synthetic?.openDays || DEFAULT_OPEN_DAYS)
                                                              .map((day) => DAY_OPTIONS.find((option) => option.value === day)?.label || day)
                                                              .join(', ') || 'none'}
                                                          </div>
                                                          <div className="ml-4 mb-2">
                                                            <div className="text-xs text-500 mb-1">Sources</div>
                                                            {['synthetic', 'community'].map((source) => (
                                                              <div key={source} className="flex align-items-center mb-1">
                                                                <Checkbox
                                                                  disabled={!modeOn}
                                                                  inputId={`${modeId}-${source}`}
                                                                  onChange={(e) => toggleSource(idx, portIdx, mode.transport_mode, source, e.checked)}
                                                                  checked={isSourceActive(idx, portIdx, mode.transport_mode, source)}
                                                                />
                                                                <label htmlFor={`${modeId}-${source}`} className="ml-2 text-sm capitalize">
                                                                  {source === 'synthetic' ? 'Synthetic' : 'Community'}
                                                                </label>
                                                              </div>
                                                            ))}
                                                            {!String(p.port_number || '').trim() && (
                                                              <small className="text-orange-600">Port number needed to inherit CBP waits</small>
                                                            )}
                                                          </div>
                                                        </div>
                                                      );
                                                    })
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        )}
                                      </div>

                                      {/* Selector de Jerarquía */}

                                    </div>
                                  </Card>
                                </div>
                              ))}
                              <div className='col-12 md:col-6'>
                                <Card style={{ minHeight: '100px' }}>
                                  <div className="p-2 " style={{ minHeight: '50px' }}>
                                    <Button type='button' label="Add Crossing Name" icon="pi pi-plus" onClick={addCrossingSection} className="p-button-text" />
                                  </div>
                                </Card>
                              </div>
                            </div>
                          </div>
                        </Panel>
                      </div>
                      <div className="col-4">
                        <Panel header="Review crossing names">
                          {
                            crossingSections.map((section, idx) => (
                              <div key={idx} className="mb-4">
                                <h2>
                                  {section?.name || `Crossing Name ${idx + 1} Example`}
                                </h2>
                                {section?.mobileName ? (
                                  <p className="text-sm text-gray-500 mt-0 mb-2">
                                    Mobile: {section.mobileName}
                                  </p>
                                ) : null}
                                <h3>
                                  {section?.description || `This is an example description for crossing name ${idx + 1}. It provides information about the crossing point and its features.`}
                                </h3>
                                <h4>
                                  URL Fragment: #{section?.urlFragment || 'no-fragment-fill'}
                                </h4>
                                <hr />
                                {
                                  section.ports.map((port, portIdx) => (
                                    <div key={portIdx} className="mb-2">
                                      <h3>
                                        Port: {port.port_name} ({port.port_number})
                                      </h3>
                                      <ul>
                                        {modesForSection(section).map((mode) => {
                                          const lanes = port.visibility?.[mode.transport_mode] || [];
                                          if (!lanes.length) return null;
                                          const src = Array.isArray(port.sources?.[mode.transport_mode])
                                            ? port.sources[mode.transport_mode]
                                            : (mode.defaultSources || []);
                                          return (
                                            <li key={mode.transport_mode}>
                                              <strong>{mode.transport_mode}:</strong> {lanes.join(', ')}
                                              <span className="text-500"> · {src.join(', ') || 'no sources'}</span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  ))
                                }
                              </div>
                            ))
                          }
                        </Panel>
                      </div>
                    </div>
                  </Card>
                  <Card subTitle="Step 3 - Configure SEO content" className='w-full mt-4'>
                    <div className='grid'>
                      <div className="col-9">
                        <Panel header="Manage Meta Tags">
                          {metaTags?.map((tag, tagIndex) => (
                            <div key={tagIndex} className="p-3 mb-3 border-round border-1 surface-border">
                              <div className="flex justify-content-between align-items-center mb-3">
                                <h4>Meta Tag {tagIndex + 1}</h4>
                                <Button
                                  icon="pi pi-trash"
                                  className="p-button-danger p-button-sm"
                                  onClick={() => removeMetaTag(tagIndex)}
                                  disabled={metaTags.length <= 1}
                                />
                              </div>
                              <label className="w-full mt-2">
                                <strong>
                                  Type
                                </strong>
                              </label>
                              <Dropdown
                                // value={selectedType}
                                value={tag.type}
                                options={tagOptions}
                                filterInputAutoFocus
                                filter
                                showClear
                                filterBy="code"
                                className="w-full mt-2 mb-2"
                                onChange={(e) => updateTagType(tagIndex, e.target.value)}
                                // onChange={(e) => {
                                //   setSelectedType(e.value)
                                // }}
                                placeholder="Select an option"
                                // itemTemplate={optionTemplate}

                                // valueTemplate={selectedOptionTemplate}
                                appendTo="self" // ← Cambia esto
                                panelStyle={{
                                  zIndex: 10000, // ← Aumenta el z-index
                                  position: 'relative'
                                }}
                              />
                              {
                                !tag.type?.self_closing && (
                                  <>
                                    <label className="w-full mt-4 mb-2">
                                      <strong>
                                        Content
                                      </strong>
                                    </label>
                                    <InputTextarea
                                      value={formatTextForTextarea(tag.content)}
                                      placeholder="Content"
                                      rows={10}
                                      className="mt-2 mb-2 w-full whitespace-pre-line"
                                      onChange={(e) => updateMainAttribute(tagIndex, 'content', e.target.value)}
                                    />
                                  </>
                                )
                              }
                              <label className="w-full mt-4">
                                <strong>
                                  Attributes
                                </strong>
                              </label>
                              {tag?.attributes?.map((attr, attrIndex) => (
                                <div key={attrIndex} className="flex align-items-center gap-2 mb-2 mt-2">
                                  <InputText
                                    value={attr.key}
                                    placeholder="Attribute name"
                                    onChange={(e) => updateAttribute(tagIndex, attrIndex, 'key', e.target.value)}
                                    className="w-1/3"
                                  />
                                  <InputText
                                    value={attr.value}
                                    placeholder="Attribute value"
                                    onChange={(e) => updateAttribute(tagIndex, attrIndex, 'value', e.target.value)}
                                    className="w-1/2"
                                  />
                                  {/* <Dropdown {...field} options={PAGE_TYPES} optionDisabled={option => option.disabled} optionLabel="name" placeholder="Select a page type" className="w-full" /> */}
                                  {/* <Dropdown options={fillSchemasOptions} optionDisabled={option => option.disabled} optionLabel="name" placeholder="Select a page type" className="w-full" />
                                  <Button
                                    icon="pi pi-arrow-left"
                                    className="p-button-green p-button-sm"
                                    tooltip='Autofill'
                                    onClick={() => removeAttribute(tagIndex, attrIndex)}
                                  /> */}
                                  <Button
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-sm"
                                    onClick={() => removeAttribute(tagIndex, attrIndex)}
                                  />
                                </div>
                              ))}

                              <Button
                                label="Add Attribute"
                                icon="pi pi-plus"
                                className="p-button-text p-button-sm"
                                onClick={() => addAttribute(tagIndex)}
                              />
                            </div>
                          ))}

                          <div className="p-2 border-round border-1 surface-border" style={{ minHeight: '50px' }}>
                            <Button type='button' label="Add Meta Tag" icon="pi pi-plus" onClick={addMetaTag} className="p-button-text" />
                          </div>
                        </Panel>
                      </div>
                      <div className="col-6">
                        <MetaTagsLiveCodePreview
                          showControl={true}
                          // fixedTags={metaGlobalTags}
                          metaTags={getFinalMetaTags}
                        />
                      </div>
                      <div className="col-12 lg:col-12">
                        <Panel header="Manage SEO Image">
                          <div className="grid">
                            <div className="col-12" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <div className="col-3">
                                <Button
                                  label={watchedImageUrl?.url ? "Change Image" : "Upload Image"}
                                  onClick={openWidget}
                                  width="200px"
                                  icon="pi pi-camera"
                                  className="p-button-primary"
                                  type="button"
                                />
                              </div>
                            </div>

                            <div className="col-12 md:col-12">
                              <label>Image URL</label>
                              <Controller
                                name="image.url"
                                control={control}
                                render={({ field }) => (
                                  <InputText
                                    {...field}
                                    className="w-full"
                                    placeholder="https://example.com/image.jpg"
                                  />
                                )}
                              />
                            </div>
                            <div className="col-12 md:col-12">
                              <label>Alt Text
                                <span className='text-sm pl-1'>
                                  (describe the image for screen readers and search engines)
                                </span>
                              </label>
                              <Controller
                                name="image.alt"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} className="w-full" placeholder="Alt Text to describe image" />
                                )}
                              />
                            </div>
                            <div className="col-12 md:col-12">
                              <label>Caption</label>
                              <Controller
                                name="image.caption"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} className="w-full" placeholder="Image caption" />
                                )}
                              />
                            </div>
                            <div className="col-12 md:col-12">
                              <h3>Image Credits
                                <span className='text-sm pl-1 text-gray-400'>
                                  (provide attribution for the image)
                                </span>
                              </h3>
                            </div>
                            <div className="col-6 md:col-6">
                              <label>Source Text</label>
                              <Controller
                                name="image.credits.source.text"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} type="text" value={field.value || ''} className="w-full" placeholder="Unplash, Shutterstock, etc" />
                                )}
                              />
                            </div>
                            <div className="col-6 md:col-6">
                              <label>Source URL</label>
                              <Controller
                                name="image.credits.source.url"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} type="text" value={field.value || ''} className="w-full" placeholder="https://source-site.com" />
                                )}
                              />
                            </div>
                            <div className="col-6 md:col-6">
                              <label>Author Text</label>
                              <Controller
                                name="image.credits.author.text"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} type="text" value={field.value || ''} className="w-full" placeholder="John Doe" />
                                )}
                              />
                            </div>
                            <div className="col-6 md:col-6">
                              <label>Author URL</label>
                              <Controller
                                name="image.credits.author.url"
                                control={control}
                                render={({ field }) => (
                                  <InputText {...field} type="text" value={field.value || ''} className="w-full" placeholder="https://author-site.com" />
                                )}
                              />
                            </div>
                            <div className="col-12 md:col-12">
                              <SeoLiveCodePreview watchedImageUrl={watchedImageUrl} />
                            </div>
                          </div>
                        </Panel>
                      </div>
                    </div>

                    {/* faqqqs */}
                    {/* <ProseMirrorEditor 
                        content={editorContent}
                        onChange={setEditorContent}
                      />

                      <ProseMirrorComponent
                        content={editorContent}
                        onChange={setEditorContent}
                      /> */}

                    <div className="grid mt-4">
                      <div className="col-6">
                        <Panel header="Manage FAQs">
                          <div className="col-12">
                            <label>
                              <strong>
                                FAQs Header
                              </strong>
                              {` <h3>`}
                            </label>
                            <Controller
                              name="faqs_section.h3"
                              control={control}
                              render={({ field }) => (
                                <InputText
                                  {...field}
                                  className="w-full"
                                  placeholder="FAQs H3"
                                  onChange={e => {
                                    field.onChange(e);
                                    trigger('faqs_section.h3');
                                  }}
                                />
                              )}
                            />
                            <div className='mt-2'>
                              <label className="mt-4">
                                <strong>
                                  FAQs description
                                </strong>
                                {` <p>`}
                              </label>
                              <Controller
                                name="faqs_section.p"
                                control={control}
                                render={({ field }) => (
                                  <InputTextarea
                                    {...field}
                                    className="w-full"
                                    rows={12}
                                    onChange={e => {
                                      field.onChange(e);
                                      trigger('faqs_section.p');
                                    }}
                                    placeholder="FAQs description"
                                  />
                                )}
                              />
                            </div>
                            <div className="col-12 justify-content-end">
                              <div className="col-4">
                                <Button
                                  icon="pi pi-plus"
                                  type='button'
                                  className="main-button"
                                  onClick={handleAddFaq}
                                  aria-label="Add FAQ"
                                  label="Add FAQ"
                                />
                              </div>
                            </div>
                            <Controller
                              name="faqs_section"
                              control={control}
                              render={() => (
                                <div>
                                  {getValues('faqs_section.items') && getValues('faqs_section.items').length > 0 && getValues('faqs_section.items').map((faq, idx) => (
                                    <div key={idx} className="mb-4 border rounded" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div className="mb-2">
                                        <span className="font-semibold">Question {idx + 1}:</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <InputText
                                          placeholder="Question"
                                          value={faq.question ?? ""}
                                          onChange={e => handleFaqChange(idx, 'question', e.target.value)}
                                          className="flex-1"
                                        />
                                        <Button
                                          icon="pi pi-trash"
                                          className="p-button-danger p-button-text"
                                          onClick={() => handleRemoveFaq(idx)}
                                          tooltip="Remove"
                                        />
                                      </div>
                                      <InputTextarea
                                        placeholder="Answer"
                                        value={faq.answer ?? ""}
                                        onChange={e => handleFaqChange(idx, 'answer', e.target.value)}
                                        rows={3}
                                        className="w-full"
                                        autoResize
                                      />

                                      <ProseMirrorComponent
                                        // key={`prosemirror-faq-${idx}`} 
                                        content={faq.answer_html || ""}
                                        onChange={(htmlContent) => {
                                          // Este htmlContent es lo que debes guardar en tu base de datos
                                          console.log('Contenido a guardar en DB:', htmlContent);
                                          handleFaqChange(idx, 'answer_html_updated', htmlContent);
                                        }}
                                      />


                                      {/* <ProseMirrorPreview
                                        content={faq.answer}
                                        className="my-custom-preview-style"
                                      /> */}

                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                          </div>
                        </Panel>
                      </div>
                      <div
                        className="col-6"
                      >
                        <Panel
                          header="FAQs review"
                        >
                          <div>
                            <h3>
                              {getValues('faqs_section.h3') || <em>Without title</em>}
                            </h3>
                            <p style={{ whiteSpace: 'pre-line' }}>
                              {getValues('faqs_section.p') || <em>Without description</em>}
                            </p>
                            {getValues('faqs_section.items') && getValues('faqs_section.items').length > 0 ? (

                              <Accordion
                                multiple
                                activeIndex={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
                              >
                                {getValues('faqs_section.items').map((faq, idx) => (
                                  <AccordionTab
                                    key={idx}
                                    header={faq.question || `No content for question ${idx + 1}`}
                                  >
                                    <div style={{ marginTop: '0.5rem', whiteSpace: 'pre-line' }}>
                                      <h4 className="mt-4">Answer Preview (unstyled):</h4>
                                      {renderAnswer(faq.answer, idx)}


                                      <h4 className="mt-4">Answer Preview (styled beta):</h4>
                                      <div
                                        style={{
                                          padding: '1rem',
                                          background: '#f5f5f5',
                                          borderRadius: '4px',
                                          marginBottom: '1rem'
                                        }}
                                        dangerouslySetInnerHTML={{ __html: faq.answer_html_updated || '<p>No content for answer...</p>' }}
                                      />
                                    </div>
                                  </AccordionTab>
                                ))}
                              </Accordion>
                            ) : (
                              <div className="text-gray-500">
                                <div>
                                  <em>No FAQs added.</em>
                                </div>
                              </div>
                            )}
                          </div>
                        </Panel>
                      </div>

                    </div>
                  </Card>
                  <div
                    className="col-12"
                    style={{
                      position: 'fixed',
                      bottom: 0,
                      width: '500px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem',
                      backgroundColor: 'white',
                      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      marginTop: '0'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        type='button'
                        label="Publish"
                        disabled={isRevalidateLoading}
                        loading={isRevalidateLoading}
                        onClick={onRevalidateUrl}
                        icon="pi pi-globe"
                        severity="warning"
                        style={{ width: '200px' }}
                      />
                    </div>

                    <Button
                      loading={isLoadingEntryPage}
                      label="Save"
                      icon="pi pi-save"
                      className="main-button"
                      type="submit"
                      style={{ width: '200px' }}
                    />
                  </div>
                </>
              )
            }

          </div>
        </div>
      </form>
    </div>
  );
}

export default RouteBuilderComponent;


